/* External */
import { memo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

/* Ott-common */
import InteractiveElement from '@dtvgo/interactive-element';

/* Providers */
import { useGtmLocalContext } from '../../providers/GtmLocalProvider';

/* Utils */
import withInteractiveFocusable from '../../hocs/withInteractiveFocusable.hoc';
import useClickCallback from '../../hooks/useClickCallback';
import { noOp } from '../../utils/functions';

function TabTitle({
  focused,
  className,
  tabId,
  label,
  onBecameActive,
  onScrolledChange,
  isSelected,
}) {
  const { gtmChangeItem } = useGtmLocalContext();
  const { formatMessage } = useIntl();
  const handleMouseDown = useClickCallback(useCallback(() => {
    onScrolledChange(true);
    onBecameActive(tabId);
  }, [onBecameActive, onScrolledChange, tabId]));

  useEffect(() => {
    if (label && isSelected) {
      gtmChangeItem({ pdpSelectedTabTitle: formatMessage({ id: label }) });
    }
  }, [formatMessage, gtmChangeItem, isSelected, label]);

  return (
    <InteractiveElement
      tag="li"
      className={`dtv-html5-c-tabs__item ${className} ${
        focused ? 'dtv-html5-c-tabs__item--focused' : ''
      } ${
        isSelected ? 'dtv-html5-c-tabs__item--selected' : ''
      }`}
      onMouseDown={handleMouseDown}
      role="menuitem"
    >
      <FormattedMessage id={label} />
    </InteractiveElement>
  );
}

TabTitle.defaultProps = {
  focused: false,
  className: '',
  onScrolledChange: noOp,
  isSelected: false,
};

TabTitle.propTypes = {
  focused: PropTypes.bool,
  className: PropTypes.string,
  tabId: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  onBecameActive: PropTypes.func.isRequired,
  onScrolledChange: PropTypes.func,
  isSelected: PropTypes.bool,
};

export default memo(withInteractiveFocusable()(memo(TabTitle)));
